import pick from 'lodash/pick';
import { Feature, TechnologyMaterial } from '@services/df/technologies';
import { ObjectModel, SpecificationInitialValues, PriceConfigForm } from '@types';
import { MaterialFields } from '@constants';
import { Executions, SpecificationFormParams } from './types';

const hasFeaturesPredicate = (model: ObjectModel) =>
    model.dfm_features &&
    Object.values(model.dfm_features).some(topologyValues => Array.isArray(topologyValues) && topologyValues.length);

export function getSpecificationOptions(model?: ObjectModel, material?: TechnologyMaterial): SpecificationFormParams {
    const fields = pick(material, MaterialFields);

    const result = Object.entries(fields)
        .filter(([key, value]) => {
            const hasOptions = !!value && Boolean(Array.isArray(value) ? value.length : Object.keys(value).length);
            // we cannot guarantee that any model does not have features
            // so we will see this in the SpecificationFeatures component
            const hasFeatures = model ? hasFeaturesPredicate(model) : true;
            return key === 'dfm_features' ? hasFeatures && hasOptions : hasOptions;
        })
        .reduce((acc, [key, value]) => {
            switch (key) {
                case 'extra_fieldsets': {
                    const options = value as SpecificationFormParams['extra_fieldsets'];
                    const groups = options.filter(list => list.options.length);
                    return groups.length ? { ...acc, [key]: groups } : acc;
                }

                case 'executions': {
                    const executions = (value as TechnologyMaterial['executions']).reduce<Executions>(
                        (accum, execution) => {
                            const executionValue = accum[execution.execution_type];
                            if (executionValue) {
                                executionValue.push(execution);
                            } else {
                                accum[execution.execution_type] = [execution];
                            }
                            return accum;
                        },
                        {},
                    );

                    return { ...acc, ...executions };
                }

                case 'thicknesses': {
                    return { ...acc, thickness: value as TechnologyMaterial['thicknesses'] };
                }

                default:
                    return { ...acc, [key]: value };
            }
        }, {} as SpecificationFormParams);

    return result;
}

export function getSpecificationInitialValues({
    formParams,
    initialThickness,
}: SpecificationInitialValues): PriceConfigForm {
    return Object.entries(formParams).reduce((acc, [key, value]) => {
        switch (key) {
            case 'dfm_features': {
                // we can omit the default value for features since the backend can choose it itself
                // plus we don't know at the moment what features are suitable for the model
                // this is resolved in the SpecificationFeatures component
                return acc;
            }
            case 'post_production': {
                return { ...acc, [key]: [] };
            }
            case 'extra_fieldsets': {
                const options = value as SpecificationFormParams['extra_fieldsets'];
                const defaultValue = options.map(val => {
                    return {
                        fieldset: val.id,
                        choice: val.options[0].id,
                    };
                });
                return { ...acc, [key]: defaultValue };
            }
            case 'thickness': {
                const options = value as SpecificationFormParams['thickness'];
                const initial = initialThickness && options.find(thickness => thickness.id === initialThickness);

                return {
                    // This fallback is for case if first material becomes not suitable after analysis
                    // or cases without preselection and returning config: null like CNC Cutter
                    ...acc,
                    [key]: (initial || options[0]).id,
                };
            }
            case 'color': {
                const options = value as SpecificationFormParams['color'];
                const firstOption = options[0]?.colors?.[0];
                if (!firstOption) return acc;
                return { ...acc, [key]: firstOption.id };
            }
            default: {
                const options = value as SpecificationFormParams[keyof SpecificationFormParams];
                return { ...acc, [key]: options[0].id };
            }
        }
    }, {} as PriceConfigForm);
}

interface GetActiveThreadsProps {
    suitableIds: string[];
    machineFeatures: Feature[];
}

interface GetActiveThreadsResult {
    treadsOptions: { value: string; label: string }[];
}

export function getThreadsOptions({ suitableIds, machineFeatures }: GetActiveThreadsProps) {
    const { treadsOptions } = suitableIds.reduce<GetActiveThreadsResult>(
        (accum, suitableFeatureId) => {
            const feature = machineFeatures.find(feature => feature.id === suitableFeatureId);
            if (feature) {
                accum.treadsOptions.push({ value: suitableFeatureId, label: feature.name });
            }
            return accum;
        },
        { treadsOptions: [] },
    );

    return treadsOptions;
}
